import React, { useState, useContext, SyntheticEvent, ReactElement } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { PlayerContext } from '../contexts/PlayerContext';
import { Typography } from '@mui/material';
import { HttpClient } from '../api/HttpClient';
import { useAlert } from '../contexts/alert/useAlert';
import { LoadingButton } from '../components/LoadingButton';
import { Category, Action } from '../analytics/Tracking';
import { InputValidatorMode, useUserNameValidator } from '../contexts/useInputValidator';
import { CreateRoomRequest, RoomResponse } from '../api/Requests';
import { useJoinRoom } from '../hooks/useRoom';
import { useTracking } from '../analytics/useTracking';
import { PreferencesService } from '../services/PreferencesService';
import { UserContext } from '../contexts/UserContext';



export default function CreateRoom(): ReactElement {
    const [validateUserName] = useUserNameValidator();
    const [userName, setUserName] = useState('');
    useContext(PlayerContext);

    const alert = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const { track } = useTracking();
    const [joinRoom] = useJoinRoom();
    const { user, authToken } = useContext(UserContext);

    function validateInput(inputUserName: string, mode: InputValidatorMode) {
        const isValid = validateUserName(inputUserName, mode).isValid;
        setIsValid(isValid);
        return isValid;
    } 

    function handleOnChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
        setUserName(event.target.value);
        validateInput(event.target.value, InputValidatorMode.onTyping);
    }

    async function handleSubmit(event: SyntheticEvent) {
        event.preventDefault();

        if (!validateInput(userName, InputValidatorMode.onSubmit)) {
            return;
        }

        setIsLoading(true);
        const httpClient = new HttpClient();
        try {
            const request = new CreateRoomRequest(userName);

            // Load preferences using PreferencesService
            // If loading fails, continue with defaults (backward compatible)
            try {
                const preferencesService = new PreferencesService(authToken);
                const preferences = await preferencesService.loadPreferences(user);

                if (preferences.defaultCardDeckType !== undefined) {
                    request.cardDeckType = preferences.defaultCardDeckType;
                }

                if (preferences.defaultPlayingCards && preferences.defaultPlayingCards.length > 0) {
                    request.playingCards = preferences.defaultPlayingCards;
                }
            } catch (preferencesError) {
                // Log but don't fail room creation if preferences can't be loaded
                console.warn("Failed to load preferences, using defaults:", preferencesError);
            }

            const response = await httpClient.post('/rooms', request) as RoomResponse;

            const player = response.players.find(p => p.name === userName);
            if (player) {
                track.event(Category.Room, Action.RoomCreated);
                joinRoom(response.urlId, player.id, userName, true);

            } else {
                setIsLoading(false);
                alert.showMessage("an unexpected error occured, please try again 🤞", "error");
            }

        } catch {
            setIsLoading(false);
            alert.showMessage("Not able to create room, please try again 🤞", "error");
        }
    }

    return (
        <div>
            <form onSubmit={handleSubmit}>
                <Grid container sx={{ padding: 3 }} spacing={3} direction="column" justifyContent="space-between" alignItems="center">
                    <Grid>
                        <Typography align="center" variant="h5">Create a planning poker room</Typography>
                        <Typography align="center" variant="subtitle1">and start estimating with your team right away</Typography>
                    </Grid>
                    <Grid><TextField autoComplete="nickname" error={!isValid} helperText={validateUserName(userName,InputValidatorMode.onTyping).errorMessage} disabled={isLoading} onChange={event => handleOnChange(event)} id="outlined-basic" label="Please enter your name" variant="outlined" color="secondary" /></Grid>
                    <Grid><LoadingButton type="submit" isLoading={isLoading}>Create Room</LoadingButton></Grid>
                </Grid>
            </form>
        </div>
    );
}